import React, { useEffect, useState } from 'react';
import { api, formatApiError } from '../../lib/api';
import ImageUploader from '../../components/admin/ImageUploader';
import TranslateButton from '../../components/admin/TranslateButton';

const empty = {
  title: '',
  subtitle: '',
  description: '',
  fullText: '',
  link: '#',
  image: '',
  title_en: '',
  subtitle_en: '',
  description_en: '',
  fullText_en: '',
};

const PhilosophyForm = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [translatingAll, setTranslatingAll] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/philosophy');
        setForm({ ...empty, ...data });
      } catch {
        /* no philosophy yet */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const translateAll = async () => {
    setTranslatingAll(true);
    setError('');
    try {
      const tasks = [
        api.post('/admin/translate', { value: form.title, target_lang: 'en' }).then(r => ['title_en', r.data.value]),
        api.post('/admin/translate', { value: form.subtitle, target_lang: 'en' }).then(r => ['subtitle_en', r.data.value]),
        api.post('/admin/translate', { value: form.description, target_lang: 'en' }).then(r => ['description_en', r.data.value]),
        api.post('/admin/translate', { value: form.fullText, target_lang: 'en' }).then(r => ['fullText_en', r.data.value]),
      ];
      const results = await Promise.all(tasks);
      setForm((f) => {
        const next = { ...f };
        for (const [k, v] of results) next[k] = v;
        return next;
      });
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setTranslatingAll(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.put('/admin/philosophy', form);
      setSuccess('Philosophie enregistrée.');
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="font-light text-gray-500">Chargement...</p>;

  return (
    <form onSubmit={onSubmit} className="max-w-4xl" data-testid="admin-philosophy-form">
      <h1 className="text-4xl font-light mb-2">Philosophie</h1>
      <p className="text-gray-500 font-light mb-8">Texte affiché sur la page Agence et la section d'accueil.</p>

      {error && <p className="text-[#d94e47] font-light mb-4" data-testid="philosophy-error">{error}</p>}
      {success && <p className="text-green-600 font-light mb-4" data-testid="philosophy-success">{success}</p>}

      <div className="space-y-6 bg-white border border-gray-200 p-8">
        <div className="flex items-center justify-between border-b border-gray-200 pb-4">
          <span className="text-xs font-light tracking-wider uppercase text-gray-500">Contenu bilingue</span>
          <button type="button" onClick={translateAll} disabled={translatingAll}
            data-testid="phil-translate-all"
            className="text-xs font-light tracking-wider uppercase bg-[#d94e47] text-white px-3 py-2 hover:bg-black transition-colors disabled:opacity-50">
            {translatingAll ? 'Traduction en cours...' : 'Traduire tout en EN (IA)'}
          </button>
        </div>

        <BilingualLine labelFr="Sous-titre" labelEn="Subtitle (EN)"
          valueFr={form.subtitle} onChangeFr={(v) => set('subtitle', v)}
          valueEn={form.subtitle_en} onChangeEn={(v) => set('subtitle_en', v)}
          testIdFr="phil-subtitle" testIdEn="phil-subtitle-en" />

        <BilingualLine labelFr="Titre" labelEn="Title (EN)"
          valueFr={form.title} onChangeFr={(v) => set('title', v)}
          valueEn={form.title_en} onChangeEn={(v) => set('title_en', v)}
          testIdFr="phil-title" testIdEn="phil-title-en" />

        <BilingualArea labelFr="Description (courte)" labelEn="Description EN (short)"
          rows={4}
          valueFr={form.description} onChangeFr={(v) => set('description', v)}
          valueEn={form.description_en} onChangeEn={(v) => set('description_en', v)}
          testIdFr="phil-description" testIdEn="phil-description-en" />

        <BilingualArea labelFr="Texte complet" labelEn="Full text (EN)"
          rows={10}
          valueFr={form.fullText} onChangeFr={(v) => set('fullText', v)}
          valueEn={form.fullText_en} onChangeEn={(v) => set('fullText_en', v)}
          testIdFr="phil-fulltext" testIdEn="phil-fulltext-en" />

        <Field label="Lien (optionnel)" value={form.link} onChange={(v) => set('link', v)} testId="phil-link" />

        <ImageUploader value={form.image} onUploaded={(url) => set('image', url)} label="Image" testId="phil-image" />
      </div>

      <div className="mt-8 flex gap-4">
        <button type="submit" disabled={saving}
          className="bg-black text-white px-8 py-3 text-sm font-light tracking-wider uppercase hover:bg-[#d94e47] transition-colors disabled:opacity-50"
          data-testid="phil-submit">
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
      </div>
    </form>
  );
};

const Field = ({ label, value, onChange, testId }) => (
  <label className="block">
    <span className="block text-xs font-light tracking-wider uppercase text-gray-500 mb-2">{label}</span>
    <input type="text" value={value ?? ''} onChange={(e) => onChange(e.target.value)}
      className="w-full border border-gray-300 px-3 py-2 font-light focus:outline-none focus:border-[#d94e47] transition-colors"
      data-testid={testId} />
  </label>
);

const BilingualLine = ({ labelFr, labelEn, valueFr, onChangeFr, valueEn, onChangeEn, testIdFr, testIdEn }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    <Field label={labelFr} value={valueFr} onChange={onChangeFr} testId={testIdFr} />
    <div className="block">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-light tracking-wider uppercase text-gray-500">{labelEn}</span>
        <TranslateButton value={valueFr} onTranslated={onChangeEn} testId={`${testIdEn}-translate`} />
      </div>
      <input type="text" value={valueEn ?? ''} onChange={(e) => onChangeEn(e.target.value)}
        className="w-full border border-gray-300 px-3 py-2 font-light focus:outline-none focus:border-[#d94e47] transition-colors"
        data-testid={testIdEn} />
    </div>
  </div>
);

const BilingualArea = ({ labelFr, labelEn, rows = 4, valueFr, onChangeFr, valueEn, onChangeEn, testIdFr, testIdEn }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    <label className="block">
      <span className="block text-xs font-light tracking-wider uppercase text-gray-500 mb-2">{labelFr}</span>
      <textarea rows={rows} value={valueFr ?? ''} onChange={(e) => onChangeFr(e.target.value)}
        className="w-full border border-gray-300 px-3 py-2 font-light focus:outline-none focus:border-[#d94e47]"
        data-testid={testIdFr} />
    </label>
    <div className="block">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-light tracking-wider uppercase text-gray-500">{labelEn}</span>
        <TranslateButton value={valueFr} onTranslated={onChangeEn} testId={`${testIdEn}-translate`} />
      </div>
      <textarea rows={rows} value={valueEn ?? ''} onChange={(e) => onChangeEn(e.target.value)}
        className="w-full border border-gray-300 px-3 py-2 font-light focus:outline-none focus:border-[#d94e47]"
        data-testid={testIdEn} />
    </div>
  </div>
);

export default PhilosophyForm;
